// Durability wire protocol for Nexus Forge (Phase 5 Step 10).
//
// Shape-only module: one type + one builder for the durable
// `itemRepaired` envelope that `player-durability.repairItem` pushes on
// success. Mirrors the shared/combat-messages.ts / shared/market-messages.ts
// pattern — no DO dispatcher wiring lives here.
//
// Classification (CLAUDE.md §"Push classification"): `itemRepaired` is
// DURABLE. The repair outcome mutates an inventory row the client
// cannot re-derive from a snapshot without re-reading inventory.

// -----------------------------------------------------------------------
// Envelope (inlined — same reason as combat-messages.ts)
// -----------------------------------------------------------------------

type Envelope<T extends string, P> = {
  type: T;
  ts: number;
  payload: P;
  seq?: number;
  eventId?: string;
};

// -----------------------------------------------------------------------
// Client → Server
// -----------------------------------------------------------------------

// Repair the durability-bearing inventory row addressed by `inventoryRowid`.
// Phase 5 Step 18 — issued from the market panel's Repair tab. The
// matching `itemRepaired` durable envelope confirms success; failures
// surface as a generic `error` envelope so the UI can clear pending state.
export type ClientRepairItem = Envelope<
  "repairItem",
  { inventoryRowid: number }
>;

export const buildRepairItemMessage = (
  inventoryRowid: number,
): ClientRepairItem => ({
  type: "repairItem",
  ts: Date.now(),
  payload: { inventoryRowid },
});

// -----------------------------------------------------------------------
// Server → Client
// -----------------------------------------------------------------------

// `inventoryRowid` pins the specific repaired instance; `itemId` carries
// the catalog id for UI labelling; `durabilityAfter` is the restored
// value (== durability_max after a successful repair).
export type ServerItemRepaired = Envelope<
  "itemRepaired",
  {
    inventoryRowid: number;
    itemId: string;
    durabilityAfter: number;
  }
>;

export type ServerDurabilityMessage = ServerItemRepaired;

// -----------------------------------------------------------------------
// Builder
// -----------------------------------------------------------------------

export const buildItemRepairedMessage = (
  inventoryRowid: number,
  itemId: string,
  durabilityAfter: number,
): ServerItemRepaired => ({
  type: "itemRepaired",
  ts: Date.now(),
  payload: { inventoryRowid, itemId, durabilityAfter },
});
